import {
  formatBatchPeriod,
  formatRecipientTypeLabel,
  isPendingDistributionBatch,
} from './revenueSharing.utils';

export const sumRevenueAmounts = (items) => {
  if (!items?.length) return '0';
  let total = 0;
  for (const item of items) {
    const n = Number(item.amount);
    if (!Number.isFinite(n)) continue;
    total += n;
  }
  return String(total);
};

export const computeTransparencyStats = (revenueItems, batches) => {
  const confirmedOrDistributed = (revenueItems || []).filter(
    (i) => i.status === 'confirmed' || i.status === 'distributed',
  );
  const totalRevenueAllTime = sumRevenueAmounts(confirmedOrDistributed);

  const distributedItems = (revenueItems || []).filter((i) => i.status === 'distributed');
  const totalDistributedRevenue = sumRevenueAmounts(distributedItems);

  const batchList = batches || [];
  const pendingCycles = batchList.filter((b) => isPendingDistributionBatch(b.status)).length;
  const executedPayouts = batchList.filter((b) => b.status === 'executed').length;

  const totalPayoutVolume = batchList
    .filter((b) => b.status === 'executed')
    .reduce((s, b) => s + (Number(b.totalRevenue) || 0), 0);

  return {
    totalRevenueAllTime,
    totalDistributedRevenue,
    pendingCycles,
    executedPayouts,
    totalPayoutVolume: String(totalPayoutVolume),
    revenueItemCount: confirmedOrDistributed.length,
  };
};

/** Sum allocations per wallet from executed distribution batches. */
export const aggregateRecipientAllocations = (batches, options = {}) => {
  const { recipientFilter = '', recipientType = '' } = options;
  const needle = recipientFilter.trim().toLowerCase();
  const byAddr = new Map();

  for (const batch of batches || []) {
    if (batch.status !== 'executed') continue;
    for (const d of batch.distributions || []) {
      const addr = (d.recipientAddress || '').toLowerCase();
      if (!addr) continue;
      if (needle && !addr.includes(needle)) continue;
      if (recipientType && d.recipientType !== recipientType) continue;

      let row = byAddr.get(addr);
      if (!row) {
        row = {
          recipientAddress: d.recipientAddress,
          recipientTypes: new Set(),
          allocatedTotal: 0,
          allocationCount: 0,
        };
        byAddr.set(addr, row);
      }
      row.recipientTypes.add(d.recipientType);
      const n = Number(d.amount);
      if (Number.isFinite(n)) row.allocatedTotal += n;
      row.allocationCount += 1;
    }
  }

  return Array.from(byAddr.values())
    .map((r) => ({
      recipientAddress: r.recipientAddress,
      recipientTypes: Array.from(r.recipientTypes),
      recipientTypeLabels: Array.from(r.recipientTypes)
        .map((t) => formatRecipientTypeLabel(t))
        .join(', '),
      allocatedTotal: String(r.allocatedTotal),
      allocationCount: r.allocationCount,
    }))
    .sort((a, b) => Number(b.allocatedTotal) - Number(a.allocatedTotal));
};

/** Merge allocation totals, claim DB summary, and optional on-chain claimable. */
export const mergeRecipientPayoutSummaries = (allocations, claimSummaries = [], claimableByAddress = {}) => {
  const claimMap = {};
  for (const c of claimSummaries || []) {
    const addr = (c.recipientAddress || '').toLowerCase();
    if (addr) claimMap[addr] = c;
  }

  const allocMap = {};
  for (const a of allocations || []) {
    const addr = (a.recipientAddress || '').toLowerCase();
    if (addr) allocMap[addr] = a;
  }

  const addrSet = new Set([...Object.keys(allocMap), ...Object.keys(claimMap)]);

  const rows = [];
  for (const addr of addrSet) {
    if (!addr) continue;
    const alloc = allocMap[addr];
    const claim = claimMap[addr];
    const allocated = Number(alloc?.allocatedTotal ?? 0);
    const claimed = Number(claim?.totalClaimed ?? 0);
    const claimableRaw = claimableByAddress[addr];
    const claimableNum =
      claimableRaw != null && claimableRaw !== '' && Number.isFinite(Number(claimableRaw))
        ? Number(claimableRaw)
        : null;

    rows.push({
      id: addr,
      recipientAddress: alloc?.recipientAddress || claim?.recipientAddress || addr,
      recipientTypeLabels: alloc?.recipientTypeLabels || '—',
      allocatedTotal: alloc?.allocatedTotal ?? '0',
      allocationCount: alloc?.allocationCount ?? 0,
      totalClaimed: claim?.totalClaimed ?? '0',
      claimCount: claim?.claimCount ?? 0,
      lastClaimedAt: claim?.lastClaimedAt ?? null,
      claimableOnChain: claimableNum != null ? String(claimableNum) : null,
      pendingEstimate:
        Number.isFinite(allocated) && Number.isFinite(claimed)
          ? String(Math.max(0, allocated - claimed))
          : null,
    });
  }

  return rows.sort((a, b) => Number(b.allocatedTotal) - Number(a.allocatedTotal));
};

/** Flatten executed batch distributions for payout ledger line items. */
export const flattenPayoutLedgerRows = (batches, recipientFilter = '') => {
  const needle = recipientFilter.trim().toLowerCase();
  const rows = [];

  for (const batch of batches || []) {
    if (batch.status !== 'executed') continue;
    const cycle = formatBatchPeriod(batch.periodStart, batch.periodEnd);
    const distributions = batch.distributions || [];
    for (const d of distributions) {
      const addr = (d.recipientAddress || '').toLowerCase();
      if (needle && !addr.includes(needle)) continue;
      rows.push({
        id: `${batch._id}-${addr}-${d.recipientType}-${d.amount}`,
        batchId: batch._id,
        cycle,
        recipientAddress: d.recipientAddress,
        recipientType: d.recipientType,
        recipientTypeLabel: formatRecipientTypeLabel(d.recipientType),
        amount: d.amount,
        txHash: batch.txHash,
        splitVersion: batch.splitVersion,
        executedAt: batch.executedAt,
      });
    }
  }

  return rows.sort((a, b) => {
    const ta = a.executedAt ? new Date(a.executedAt).getTime() : 0;
    const tb = b.executedAt ? new Date(b.executedAt).getTime() : 0;
    return tb - ta;
  });
};
